import { useEffect, useRef, useState } from 'react';

export interface UseAsyncResourceOptions {
  /**
   * Keep the last successful value when a fetch fails instead of resetting to
   * `emptyValue`.
   *
   * Polling hooks need this: a single transient failure would otherwise blank
   * the view, and — where the poll condition is derived from the data itself —
   * stop the poll permanently, since there is no longer any data to say the
   * resource is still in flight.
   */
  keepPreviousDataOnError?: boolean;
}

export interface UseAsyncResourceReturn<T> {
  data: T;
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
}

/**
 * Shared async-fetch skeleton for the module's data hooks: runs `fetcher`
 * in an effect, tracks `isLoading`/`hasError`, ignores responses that
 * resolve after the effect was cleaned up (stale request or unmount), and
 * exposes `refetch()` to re-run the fetch on demand.
 *
 * `fetcher` drives the effect's dependency array, so it must be a stable
 * reference — wrap it in `useCallback` (with its own params as deps) in the
 * calling hook rather than passing a new closure every render.
 */
export const useAsyncResource = <T>(
  fetcher: () => Promise<T>,
  emptyValue: T,
  { keepPreviousDataOnError = false }: UseAsyncResourceOptions = {},
): UseAsyncResourceReturn<T> => {
  const [data, setData] = useState<T>(emptyValue);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [refetchToken, setRefetchToken] = useState(0);

  // Captured in a ref (rather than read from the `emptyValue` param
  // directly) so callers can pass an inline empty value without needing to
  // memoize it — reading `.current` in the effect below does not require
  // adding it to the dependency array.
  const emptyValueRef = useRef(emptyValue);
  emptyValueRef.current = emptyValue;

  useEffect(() => {
    let isCurrent = true;

    setIsLoading(true);
    setHasError(false);

    fetcher()
      .then(result => {
        if (!isCurrent) {
          return;
        }

        setData(result);
      })
      .catch(() => {
        if (!isCurrent) {
          return;
        }

        setHasError(true);

        if (!keepPreviousDataOnError) {
          setData(emptyValueRef.current);
        }
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [fetcher, refetchToken, keepPreviousDataOnError]);

  const refetch = (): void => {
    setRefetchToken(previous => previous + 1);
  };

  return { data, isLoading, hasError, refetch };
};
